import { useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { setUser } from "../lib/authSlice";
import ImpersonationBanner from "../components/ImpersonationBanner";

import Dashboard from './admin/dashboard';
import Listings from './admin/listings';
import ListingDetail from './admin/listings/[lid]';
import ContactEditForm from "../components/listingDetail/Edit/ContactEditForm";
import HoursEditForm from "../components/listingDetail/Edit/HoursEditForm";
import ProfileEditForm from "../components/listingDetail/Edit/ProfileEditForm";
import AddressEditForm from "../components/listingDetail/Edit/AddressEditForm";
import AmenitiesEditForm from "../components/listingDetail/Edit/AmenitiesEditForm";
import HolidayHoursEditForm from "../components/listingDetail/Edit/HolidayHoursEditForm";
import ListingForm from './admin/listings/[lid]/edit';
import Users from './admin/users';
import Suggestions from './admin/suggestions';
import Reports from './admin/reports';
import UserDetail from './admin/users/[uid]';
import UserForm from './admin/users/[uid]/edit';
import DataManagement from './admin/data-management';
import client from '../lib/feathers';

import Sidebar from '../components/AdminSidebar';

import Notifications from "./admin/notifications";
import NoResultsSearch from "./admin/reports/no-results-search";
import QRCode from "./admin/QRCode";
import NoListingLibations from "./admin/reports/no-listing-libations";
import ListingSubmissions from "./admin/reports/listing-submissions";
import WinesNeedingReview from "./admin/reports/wines-needing-review";
import PendingUserInvitations from "./admin/reports/pending-user-invitations";
import SignupSummary from "./admin/reports/signup-summary";
import PhotoReports from "./admin/reports/photo-reports";
import Subscriptions from "./admin/reports/subscriptions";
import UserLibrary from "./admin/reports/user-library";
import BottleScans from "./admin/reports/bottle-scans";
import DuplicateImages from "./admin/reports/duplicate-images";
import DuplicateLibations from "./admin/reports/duplicate-libations";
import LibationsWithoutImages from "./admin/reports/libations-without-images";
import ImageCandidates from "./admin/reports/image-candidates";
import BottleScanDetail from "./admin/reports/bottle-scan-detail";
import ListingDataHealth from "./admin/reports/listing-data-health";
import ListingDataHealthPendingApproval from "./admin/reports/listing-data-health-pending-approval";
import ListingDataHealthRun from "./admin/reports/listing-data-health-run";
import VIPPartners from "./admin/vip-partners";
import Passports from "./admin/passports";
import PassportNew from "./admin/passports/new";
import PassportDetail from "./admin/passports/[pid]";
import PassportEdit from "./admin/passports/[pid]/edit";
import Appellations from "./admin/appellations";
import AppellationDetail from "./admin/appellations/[aid]";
import AppellationEdit from "./admin/appellations/[aid]/edit";
import AccessPasses from "./admin/access-passes";
import AccessPassNew from "./admin/access-passes/new";
import AccessPassDetail from "./admin/access-passes/[apid]";
import AccessPassEdit from "./admin/access-passes/[apid]/edit";
import AvaConversations from "./admin/ava-conversations";
import AvaConversationDetail from "./admin/ava-conversations/[sessionId]";
import OutreachDashboard from "./admin/outreach";
import OutreachContacts from "./admin/outreach/contacts";
import OutreachCampaigns from "./admin/outreach/campaigns";
import OutreachCampaignDetail from "./admin/outreach/campaigns/[cid]";
import OutreachCampaignNew from "./admin/outreach/campaigns/new";
import OutreachJourneys from "./admin/outreach/journeys";
import OutreachJourneyNew from "./admin/outreach/journeys/new";
import OutreachJourneyDetail from "./admin/outreach/journeys/[jid]";
import UserLibraryImport from "./admin/users/[uid]/library-import";
import LibraryImportReview from "./admin/library-imports/[id]";
import LibraryImportsIndex from "./admin/library-imports";
import Broadcasts from "./admin/broadcasts";
import NewBroadcast from "./admin/broadcasts/new";
import Campaigns from "./admin/campaigns";
import NewCampaign from "./admin/campaigns/new";
import ContactDetail from "./admin/contacts/[contactId]";
import EmailTemplates from "./admin/email-templates";
import EmailTemplateForm from "./admin/email-templates/new";

function Admin() {
  const [showSidebar, setShowSidebar] = useState(false);
  const user = useSelector((state) => state.auth.user);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const auth = await client.authenticate();
        dispatch(setUser(auth.user));
        if (auth.user.role !== 'admin') {
          navigate(`/${auth.user.role}/`);
        }
      } catch (e) {
        navigate(`/`);
      }
    };
    checkAuth();
  }, []);

  return (
    <div className="App">

      {/* Sits above everything: an admin acting as someone else must never lose sight of it. */}
      <ImpersonationBanner />
      {user
        ? (
          <div className="h-screen-safe flex overflow-hidden bg-gray-100">
            {/* <!-- Off-canvas menu for mobile, show/hide based on off-canvas menu state. --> */}
            {showSidebar
              && (
                <div className="md:hidden">
                  <div className="fixed inset-0 flex z-40">

                    <div className="fixed inset-0">
                      <div className="absolute inset-0 bg-gray-600 opacity-75" />
                    </div>

                    <div className="relative flex-1 flex flex-col max-w-xs w-full bg-primary" onClick={() => setShowSidebar(false)}>
                      <div className="absolute top-0 right-0 -mr-12 pt-2">
                        <button type="button" className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                          <span className="sr-only">Close sidebar</span>
                          {/* <!-- Heroicon name: outline/x --> */}
                          <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <Sidebar user={user} />
                    </div>
                    <div className="flex-shrink-0 w-14">
                      {/* <!-- Force sidebar to shrink to fit close icon --> */}
                    </div>
                  </div>
                </div>
              )}

            {/* <!-- Static sidebar for desktop --> */}
            <div className="hidden md:flex md:flex-shrink-0">
              <div className="flex flex-col w-64">
                <Sidebar user={user} />
              </div>
            </div>
            <div className="flex flex-col w-0 flex-1 overflow-hidden">
              <div className="md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3">
                <button type="button" onClick={() => setShowSidebar(true)} className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                  <span className="sr-only">Open sidebar</span>
                  {/* <!-- Heroicon name: outline/menu --> */}
                  <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
              {/* pt-4 on mobile gives page content breathing room below
                  the hamburger button row (which sits flush above main).
                  Desktop padding is unchanged. */}
              <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none px-2 pt-4 pb-2 md:p-5">
                <Routes>
                  <Route path="/data-management/*" element={<DataManagement />} />
                  <Route path="/users" element={<Users />} />
                  <Route path="/users/:uid/edit" element={<UserForm />} />
                  <Route path="/users/:uid/library-import" element={<UserLibraryImport />} />
                  <Route path="/users/:uid" element={<UserDetail />} />
                  <Route path="/library-imports" element={<LibraryImportsIndex />} />
                  <Route path="/library-imports/:id" element={<LibraryImportReview />} />
                  <Route path="/campaigns" element={<Campaigns />} />
                  <Route path="/campaigns/new" element={<NewCampaign />} />
                  <Route path="/contacts/:contactId" element={<ContactDetail />} />
                  <Route path="/email-templates" element={<EmailTemplates />} />
                  <Route path="/email-templates/new" element={<EmailTemplateForm />} />
                  <Route path="/email-templates/:id" element={<EmailTemplateForm />} />
                  <Route path="/broadcasts" element={<Broadcasts />} />
                  <Route path="/broadcasts/new" element={<NewBroadcast />} />
                  <Route path="/suggestions" element={<Suggestions />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/reports/no-results-search" element={<NoResultsSearch />} />
                  <Route path="/reports/no-listing-libations" element={<NoListingLibations />} />
                  <Route path="/reports/listing-submissions" element={<ListingSubmissions />} />
                  <Route path="/reports/wines-needing-review" element={<WinesNeedingReview />} />
                  <Route path="/reports/pending-user-invitations" element={<PendingUserInvitations />} />
                  <Route path="/reports/signup-summary" element={<SignupSummary />} />
                  <Route path="/reports/photo-reports" element={<PhotoReports />} />
                  <Route path="/reports/subscriptions" element={<Subscriptions />} />
                  <Route path="/reports/user-library" element={<UserLibrary />} />
                  <Route path="/reports/bottle-scans" element={<BottleScans />} />
                  <Route path="/reports/bottle-scans/:scanId" element={<BottleScanDetail />} />
                  <Route path="/reports/duplicate-images" element={<DuplicateImages />} />
                  <Route path="/reports/duplicate-libations" element={<DuplicateLibations />} />
                  <Route path="/reports/libations-without-images" element={<LibationsWithoutImages />} />
                  <Route path="/reports/image-candidates" element={<ImageCandidates />} />
                  <Route path="/reports/listing-data-health" element={<ListingDataHealth />} />
                  <Route path="/reports/listing-data-health/pending-approval" element={<ListingDataHealthPendingApproval />} />
                  <Route path="/reports/listing-data-health/run/:runId" element={<ListingDataHealthRun />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/listings" element={<Listings />} />
                  {/* Focused one-section forms, shared with the industry app.
                      Must sit ABOVE the whole-listing form or /edit/contact is
                      swallowed by it. */}
                  <Route path="/listings/:lid/edit/contact" element={<ContactEditForm />} />
                  <Route path="/listings/:lid/edit/hours" element={<HoursEditForm />} />
                  <Route path="/listings/:lid/edit/profile" element={<ProfileEditForm />} />
                  <Route path="/listings/:lid/edit/address" element={<AddressEditForm />} />
                  <Route path="/listings/:lid/edit/amenities" element={<AmenitiesEditForm />} />
                  <Route path="/listings/:lid/edit/holidays" element={<HolidayHoursEditForm />} />
                  <Route path="/listings/:lid/edit" element={<ListingForm />} />
                  <Route path="/listings/:lid/*" element={<ListingDetail />} />
                  <Route path="/vip-partners" element={<VIPPartners />} />
                  <Route path="/appellations" element={<Appellations />} />
                  <Route path="/appellations/new" element={<AppellationEdit />} />
                  <Route path="/appellations/:aid/edit" element={<AppellationEdit />} />
                  <Route path="/appellations/:aid" element={<AppellationDetail />} />
                  <Route path="/access-passes" element={<AccessPasses />} />
                  <Route path="/access-passes/new" element={<AccessPassNew />} />
                  <Route path="/access-passes/:apid/edit" element={<AccessPassEdit />} />
                  <Route path="/access-passes/:apid" element={<AccessPassDetail />} />
                  <Route path="/passports" element={<Passports />} />
                  <Route path="/passports/new" element={<PassportNew />} />
                  <Route path="/passports/:pid/edit" element={<PassportEdit />} />
                  <Route path="/passports/:pid" element={<PassportDetail />} />
                  <Route path="/outreach" element={<OutreachDashboard />} />
                  <Route path="/outreach/contacts" element={<OutreachContacts />} />
                  <Route path="/outreach/campaigns" element={<OutreachCampaigns />} />
                  <Route path="/outreach/campaigns/new" element={<OutreachCampaignNew />} />
                  <Route path="/outreach/campaigns/:cid" element={<OutreachCampaignDetail />} />
                  <Route path="/outreach/journeys" element={<OutreachJourneys />} />
                  <Route path="/outreach/journeys/new" element={<OutreachJourneyNew />} />
                  <Route path="/outreach/journeys/:jid" element={<OutreachJourneyDetail />} />
                  <Route path="/ava-conversations" element={<AvaConversations />} />
                  <Route path="/ava-conversations/:sessionId" element={<AvaConversationDetail />} />
                  <Route path="/qrcode" element={<QRCode />} />
                  <Route path="/" element={<Dashboard />} />
                </Routes>
              </main>
            </div>
          </div>)
        : <div>Checking Authentication...</div>}

    </div>
  )
}

export default Admin;